import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { usePreferences } from '../hooks/usePreferences';
import { wouldChangeSize } from '../store/restrainStore';
import './OrderLine.css';
import './Restrain/Restrain.css';

export interface OrderLineProps {
  id: string;
  price: number;
  yPosition: number;
  currentPrice: number;
  onPlaceOrder: (type: 'BUY_LIMIT' | 'SELL_LIMIT' | 'BUY_STOP' | 'SELL_STOP', price: number, quantity: number) => void;
  onRemove: (id: string) => void;
  onDrag: (id: string, newYPosition: number) => void;
  useOCO?: boolean;
  ocoTPOffset?: number;
  ocoSLOffset?: number;
  /**
   * Restrain: the restrained position's netPos and the refusal text. Buttons
   * whose order would change that size are disabled with this as tooltip.
   */
  restrainNetPos?: number;
  restrainMessage?: string | null;
}

export interface OrderLineHandle {
  updateY: (y: number) => void;
}

const OrderLine = forwardRef<OrderLineHandle, OrderLineProps>((
  {
    id,
    price,
    yPosition,
    currentPrice,
    onPlaceOrder,
    onRemove,
    onDrag,
    useOCO = false,
    ocoTPOffset = 10,
    ocoSLOffset = 5,
    restrainNetPos = 0,
    restrainMessage = null,
  },
  ref
) => {
  // Quantity is sourced from trading panel preferences — single source of truth.
  // Editing the input updates the preference, which keeps TradingPanel and any other
  // OrderLine instances in lockstep.
  const { preferences, updatePreference } = usePreferences();
  const quantity = preferences.tradingPanel?.quantity ?? 1;
  const setQuantity = (q: number) => updatePreference('tradingPanel', { quantity: Math.max(1, q) });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef<number>(0);
  const initialY = useRef<number>(0);
  const lineElRef = useRef<HTMLDivElement>(null);
  const controlsElRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);

  useEffect(() => { isDraggingRef.current = isDragging; }, [isDragging]);

  useImperativeHandle(ref, () => ({
    updateY: (y: number) => {
      if (isDraggingRef.current) return;
      if (lineElRef.current) lineElRef.current.style.top = `${y}px`;
      if (controlsElRef.current) controlsElRef.current.style.top = `${y}px`;
    },
  }));


  // Determine which order buttons to show based on price relationship
  // When price > currentPrice (above market):
  //   - BUY STOP (buy when price goes up)
  //   - SELL LIMIT (sell at higher price)
  // When price < currentPrice (below market):
  //   - BUY LIMIT (buy at lower price)
  //   - SELL STOP (sell when price goes down)
  const orderLineAboveMarket = price > currentPrice;

  const restrainLockFor = (type: 'BUY_LIMIT' | 'SELL_LIMIT' | 'BUY_STOP' | 'SELL_STOP'): string | null =>
    restrainMessage && wouldChangeSize(restrainNetPos, type.startsWith('BUY') ? 'Buy' : 'Sell', quantity)
      ? restrainMessage
      : null;

  const handlePlaceOrder = (type: 'BUY_LIMIT' | 'SELL_LIMIT' | 'BUY_STOP' | 'SELL_STOP') => {
    if (restrainLockFor(type)) return;
    onPlaceOrder(type, price, quantity);
    onRemove(id); // Remove the line after placing order
  };

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    // Don't start drag if clicking on a button or input
    if ((e.target as HTMLElement).tagName === 'BUTTON' || (e.target as HTMLElement).tagName === 'INPUT') {
      return;
    }

    setIsDragging(true);
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    dragStartY.current = clientY;
    initialY.current = yPosition;
    // Don't preventDefault on touchstart to allow scrolling detection
    if (e.type !== 'touchstart') {
      e.preventDefault(); // Prevent text selection while dragging
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      const deltaY = e.clientY - dragStartY.current;
      const newY = initialY.current + deltaY;

      // Call parent's onDrag to update the position
      onDrag(id, newY);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging) return;

      // Prevent scrolling while dragging
      e.preventDefault();

      const touch = e.touches[0];
      const deltaY = touch.clientY - dragStartY.current;
      const newY = initialY.current + deltaY;

      // Call parent's onDrag to update the position
      onDrag(id, newY);
    };

    const handleDragEnd = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleDragEnd);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleDragEnd);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleDragEnd);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleDragEnd);
      };
    }
  }, [isDragging, id, onDrag]);

  return (
    <>
      {/* Horizontal price line across the chart */}
      <div
        className={`order-line-horizontal ${isDragging ? 'dragging' : ''}`}
        ref={lineElRef}
        style={{
          top: `${yPosition}px`,
        }}
      >
        {/* Cancel button on the line */}
        <button
          className="order-line-cancel"
          onClick={() => onRemove(id)}
          title="Remove Line"
        >
          ×
        </button>
      </div>
      {/* Control panel */}
      <div
        className={`order-line-controls ${isDragging ? 'dragging' : ''}`}
        ref={controlsElRef}
        style={{
          top: `${yPosition}px`,
          right: '80px',
          transform: 'translateY(-50%)',
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        <div className="order-line-price">
          {price.toFixed(2)}
        </div>

        <div className="order-line-quantity">
          <span className="order-line-quantity-label">Qty:</span>
          <input
            type="number"
            className="order-line-quantity-input"
            value={quantity}
            onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
            min="1"
            step="1"
          />
        </div>

        {useOCO && (
          <div className="order-line-oco-badge" title={`OCO: TP ${ocoTPOffset} / SL ${ocoSLOffset}`}>
            OCO
          </div>
        )}

        <div className="order-line-buttons">
          {orderLineAboveMarket ? (
            // Above market: BUY STOP and SELL LIMIT
            <>
              <button
                className={`order-line-btn buy-stop${restrainLockFor('BUY_STOP') ? ' restrain-locked' : ''}`}
                onClick={() => handlePlaceOrder('BUY_STOP')}
                disabled={!!restrainLockFor('BUY_STOP')}
                title={restrainLockFor('BUY_STOP') ?? 'Buy Stop - Buy when price reaches this level'}
              >
                Buy Stop
              </button>
              <button
                className={`order-line-btn sell-limit${restrainLockFor('SELL_LIMIT') ? ' restrain-locked' : ''}`}
                onClick={() => handlePlaceOrder('SELL_LIMIT')}
                disabled={!!restrainLockFor('SELL_LIMIT')}
                title={restrainLockFor('SELL_LIMIT') ?? 'Sell Limit - Sell at this higher price'}
              >
                Sell Limit
              </button>
            </>
          ) : (
            // Below market: BUY LIMIT and SELL STOP
            <>
              <button
                className={`order-line-btn buy-limit${restrainLockFor('BUY_LIMIT') ? ' restrain-locked' : ''}`}
                onClick={() => handlePlaceOrder('BUY_LIMIT')}
                disabled={!!restrainLockFor('BUY_LIMIT')}
                title={restrainLockFor('BUY_LIMIT') ?? 'Buy Limit - Buy at this lower price'}
              >
                Buy Limit
              </button>
              <button
                className={`order-line-btn sell-stop${restrainLockFor('SELL_STOP') ? ' restrain-locked' : ''}`}
                onClick={() => handlePlaceOrder('SELL_STOP')}
                disabled={!!restrainLockFor('SELL_STOP')}
                title={restrainLockFor('SELL_STOP') ?? 'Sell Stop - Sell when price reaches this level'}
              >
                Sell Stop
              </button>
            </>
          )}
          <button
            className="order-line-btn close"
            onClick={() => onRemove(id)}
            title="Remove Line"
          >
            ×
          </button>
        </div>
      </div>
    </>
  );
});

export default OrderLine;
